import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Plus, Search, Filter, ChevronRight, Calendar, DollarSign,
  Target, Clock, Award, TrendingUp, X
} from 'lucide-react';
import { toast } from 'sonner';
import CRMBidDetail from './CRMBidDetail';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Lead': 'bg-gray-100 text-gray-700',
  'Qualified': 'bg-blue-100 text-blue-700',
  'Bidding': 'bg-amber-100 text-amber-700',
  'Proposal Submitted': 'bg-purple-100 text-purple-700',
  'Negotiation': 'bg-indigo-100 text-indigo-700',
  'Awarded': 'bg-emerald-100 text-emerald-700',
  'Lost': 'bg-red-100 text-red-700',
};

const AWARD_COLORS = {
  'Pending': 'bg-yellow-100 text-yellow-700',
  'Awarded': 'bg-emerald-100 text-emerald-700',
  'Lost': 'bg-red-100 text-red-700',
  'Cancelled': 'bg-gray-100 text-gray-500',
  'No Result Yet': 'bg-gray-50 text-gray-400',
};

const fmt = (v) => v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) : '—';
const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return v; }
};

const StatCard = ({ icon: Icon, label, value, color = 'text-slate-900' }) => (
  <Card>
    <CardContent className="pt-5 pb-4">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-slate-100 rounded-lg"><Icon size={20} className="text-slate-600" /></div>
        <div>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          <p className="text-xs text-gray-500 font-medium">{label}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const CRMBids = () => {
  const [bids, setBids] = useState([]);
  const [stats, setStats] = useState({});
  const [enums, setEnums] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const qs = params.toString() ? `?${params}` : '';
      const [bidsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/crm/bids${qs}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/crm/bids/stats`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setBids(bidsRes.bids || []);
      setStats(statsRes);
    } catch { toast.error('Failed to load bids'); }
    finally { setLoading(false); }
  }, [search, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API}/api/crm/bids/enums`, { credentials: 'include' })
      .then(r => r.json()).then(setEnums).catch(() => {});
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/crm/bids`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_name: 'New Bid', status: 'Lead' }),
      });
      const bid = await res.json();
      toast.success(`Created ${bid.bid_number}`);
      setSelectedBid(bid.id);
      load();
    } catch { toast.error('Failed to create bid'); }
    finally { setCreating(false); }
  };

  const clearFilters = () => { setFilters({}); setSearch(''); };
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  if (selectedBid) {
    return <CRMBidDetail bidId={selectedBid} enums={enums} onBack={() => { setSelectedBid(null); load(); }} />;
  }

  return (
    <div className="space-y-5">
      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={Target} label="Open Bids" value={stats.open_bids ?? 0} />
        <StatCard icon={Clock} label="Due This Week" value={stats.due_this_week ?? 0} color={stats.due_this_week > 0 ? 'text-amber-600' : 'text-slate-900'} />
        <StatCard icon={Award} label="Submitted / Awaiting" value={stats.submitted_awaiting ?? 0} />
        <StatCard icon={TrendingUp} label="Win Rate" value={`${stats.win_rate ?? 0}%`} color={stats.win_rate >= 50 ? 'text-emerald-600' : 'text-slate-900'} />
        <StatCard icon={DollarSign} label="Total Bid Value" value={fmt(stats.total_bid_value)} color="text-emerald-700" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="bid-search"
            placeholder="Search bids..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="bid-filter-toggle">
          <Filter size={14} className="mr-1.5" /> Filters
          {activeFilterCount > 0 && <Badge className="ml-1.5 bg-blue-900 text-white text-xs px-1.5">{activeFilterCount}</Badge>}
        </Button>
        <Button size="sm" className="bg-blue-900 hover:bg-blue-800 ml-auto" onClick={handleCreate} disabled={creating} data-testid="create-bid-btn">
          <Plus size={14} className="mr-1.5" /> New Bid
        </Button>
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <select data-testid="filter-status" value={filters.status || ''} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                className="px-3 py-1.5 border rounded-md text-sm bg-white">
                <option value="">All Statuses</option>
                {(enums.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="filter-work-type" value={filters.work_type || ''} onChange={e => setFilters(f => ({ ...f, work_type: e.target.value }))}
                className="px-3 py-1.5 border rounded-md text-sm bg-white">
                <option value="">All Work Types</option>
                {(enums.work_types || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="filter-award" value={filters.award_status || ''} onChange={e => setFilters(f => ({ ...f, award_status: e.target.value }))}
                className="px-3 py-1.5 border rounded-md text-sm bg-white">
                <option value="">All Awards</option>
                {(enums.award_statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <Input placeholder="Estimator..." value={filters.estimator || ''} onChange={e => setFilters(f => ({ ...f, estimator: e.target.value }))} className="h-8 text-sm" />
              <Input placeholder="Agency..." value={filters.agency || ''} onChange={e => setFilters(f => ({ ...f, agency: e.target.value }))} className="h-8 text-sm" />
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
                <X size={14} className="mr-1" /> Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bids Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bids-table">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left px-4 py-3 font-medium text-slate-600">Bid #</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Project</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Agency</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Work Type</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Bidding As</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">License Req.</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Bid Due</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Estimator</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">Amount</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></td></tr>
              ) : bids.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-12 text-gray-400">No bids found. Click "+ New Bid" to create one.</td></tr>
              ) : bids.map(b => (
                <tr key={b.id} onClick={() => setSelectedBid(b.id)} className="border-b hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`bid-row-${b.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{b.bid_number}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 max-w-[200px] truncate">{b.project_name}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[150px] truncate">{b.agency || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{b.work_type || '—'}</td>
                  <td className="px-4 py-3" data-testid={`bid-bidding-as-${b.id}`}>
                    {b.bidding_as === 'Prime' ? (
                      <Badge className="text-xs bg-blue-100 text-blue-700">Prime</Badge>
                    ) : b.bidding_as === 'Sub' ? (
                      <Badge className="text-xs bg-purple-100 text-purple-700">Sub</Badge>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs font-mono" data-testid={`bid-license-${b.id}`}>{b.license_requirement || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{fmtDate(b.bid_due_at)}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{b.estimator_name || '—'}</td>
                  <td className="px-4 py-3">
                    <Badge className={`text-xs ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'}`}>{b.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">{fmt(b.bid_amount)}</td>
                  <td className="px-4 py-3"><ChevronRight size={14} className="text-gray-300" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Summary footer */}
      <div className="flex items-center justify-between text-xs text-gray-400 px-1">
        <span>{bids.length} bid{bids.length !== 1 ? 's' : ''} shown</span>
        <span>Won: {stats.awarded_count || 0} | Lost: {stats.lost_count || 0} | Open Value: {fmt(stats.total_open_value)}</span>
      </div>
    </div>
  );
};

export default CRMBids;
